// LoginPage — explicit login entry point at /login.
//
// Security: only window.location.pathname + search are forwarded — no scheme or
// host, preventing open-redirect. The backend _safe_next_url() sanitises it too.

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { apiUrl } from '@/shared/config';
import { AuthLayout } from '../auth/AuthLayout';
import oktaIcon from '../../../assets/images/okta-icon.svg';
import adobeLogo from '../../../assets/images/Adobe_icon_RGB_red.svg';

function isAuthRoute(path: string): boolean {
  const appPath = path.toLowerCase().replace(/^\/preview\/pr-[1-9][0-9]*(?=\/|$)/, '');
  return appPath === '/login' || appPath.startsWith('/auth/') || appPath.startsWith('/api/auth/');
}

function buildLoginUrl(): string {
  // The session guard redirects here as /login?next=<original path>. Prefer that;
  // fall back to the current path if a user hit /login directly on a real page.
  const param = new URLSearchParams(window.location.search).get('next');
  const next = param ?? window.location.pathname + window.location.search + window.location.hash;
  // Only forward a same-site absolute path (single leading slash) — no scheme,
  // host, protocol-relative //host, or backslash variant (\ normalizes to / in
  // some browsers, making /\host an open redirect), preventing open-redirect.
  // Reject login routes case-insensitively to avoid bouncing back to sign-in.
  // Backend _safe_next_url() sanitises it too.
  if (
    !next.startsWith('/') ||
    next.startsWith('//') ||
    next.includes('\\') ||
    isAuthRoute(new URL(next, window.location.origin).pathname)
  ) {
    return apiUrl('/auth/login');
  }
  return `${apiUrl('/auth/login')}?next=${encodeURIComponent(next)}`;
}

export function LoginPage() {
  const handleSignIn = () => {
    window.location.assign(buildLoginUrl());
  };

  return (
    <AuthLayout headingId="login-heading">
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <img
            src={adobeLogo}
            alt="Adobe"
            width={72}
            height={72}
            className="shrink-0"
          />
          <p className="text-2xl font-bold text-foreground">ACS Agent Hub</p>
        </div>
        <Card className="mx-auto w-full border-border/80 shadow-sm">
          <CardHeader className="items-center pb-4 text-center">
            <CardTitle id="login-heading" className="text-2xl font-semibold text-foreground">Sign in</CardTitle>
            <CardDescription className="sr-only">Continue with single sign-on</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Button size="lg" className="h-11 w-full" onClick={handleSignIn}>
              <img
                src={oktaIcon}
                alt=""
                aria-hidden="true"
                className="size-4 shrink-0 brightness-0 invert dark:brightness-0 dark:invert"
              />
              Continue with Okta
            </Button>
          </CardContent>
          <CardFooter className="p-0" />
        </Card>
      </div>
    </AuthLayout>
  );
}
